/**
 * Newsletter im Konto (seit 26.10.2026, newsletter.ts im Supabase-Teil).
 *
 * Anmelden schickt nur die Mail fürs Double-Opt-in; ein Abo wird es erst mit
 * dem Klick darin. Der Wortlaut neben dem Knopf ist die Einwilligung und steht
 * gleich in `EINWILLIGUNG` (newsletter.ts); wer ihn ändert, ändert beide und
 * zählt dort FASSUNG hoch.
 */

import { useState } from 'react';
import { ApiFehler, setzeNewsletter, type NewsletterStand } from '../api';
import { Hinweis, KleinerKnopf } from '../ui';
import { t } from '../sprache';

export const EINWILLIGUNG_TEXT = () => t(
  'Newsletter von 99takes per E-Mail: Neuigkeiten und Tipps zum Testen von Texten, etwa einmal im Monat. Abmelden jederzeit.',
  'The 99takes newsletter by email: news and tips on testing copy, about once a month. Unsubscribe at any time.',
);

export default function NewsletterZeile({ stand, geaendert }: { stand: NewsletterStand; geaendert: (s: NewsletterStand) => void }) {
  const [laedt, setLaedt] = useState(false);
  const [meldung, setMeldung] = useState<{ art: 'gut' | 'schlecht'; text: string } | null>(null);

  async function setzen(an: boolean) {
    setLaedt(true);
    setMeldung(null);
    try {
      const r = await setzeNewsletter(an);
      geaendert(r.newsletter);
      if (an && r.mail === 'verschickt') setMeldung({ art: 'gut', text: t('Wir haben dir eine Mail geschickt. Bestätige darin die Anmeldung.', 'We sent you an email. Confirm your subscription in it.') });
      if (an && r.mail === 'schon_verschickt') setMeldung({ art: 'gut', text: t('Die Mail ist schon unterwegs. Schau auch im Spam-Ordner nach.', 'The email is already on its way. Check your spam folder too.') });
      if (an && r.mail === 'nicht_verschickt') setMeldung({ art: 'schlecht', text: t('Die Mail ließ sich gerade nicht verschicken. Versuch es später noch einmal.', 'The email could not be sent right now. Please try again later.') });
      if (!an) setMeldung({ art: 'gut', text: t('Du bist vom Newsletter abgemeldet.', 'You are unsubscribed from the newsletter.') });
    } catch (err) {
      setMeldung({ art: 'schlecht', text: err instanceof ApiFehler ? err.message : t('Das hat nicht geklappt. Versuch es noch einmal.', 'That did not work. Please try again.') });
    } finally {
      setLaedt(false);
    }
  }

  const unter = stand === 'an'
    ? t('Du bekommst den Newsletter.', 'You receive the newsletter.')
    : stand === 'offen'
      ? t('Die Bestätigung steht noch aus. Den Link dazu haben wir dir per Mail geschickt.', 'Confirmation is pending. We sent you the link by email.')
      : EINWILLIGUNG_TEXT();

  return (
    <div className="border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:px-5">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5">
        <span className="min-w-0 max-w-xl">
          <span className="block text-[0.9375rem] font-semibold text-tinte">{t('Newsletter', 'Newsletter')}</span>
          <span className="mt-0.5 block text-[0.8125rem] text-text-hell">{unter}</span>
        </span>
        {stand === 'an' && <KleinerKnopf type="button" onClick={() => void setzen(false)} disabled={laedt}>{t('Abmelden', 'Unsubscribe')}</KleinerKnopf>}
        {stand === 'offen' && <KleinerKnopf type="button" onClick={() => void setzen(true)} disabled={laedt}>{t('Mail erneut senden', 'Resend email')}</KleinerKnopf>}
        {stand === 'aus' && <KleinerKnopf type="button" onClick={() => void setzen(true)} disabled={laedt}>{t('Anmelden', 'Subscribe')}</KleinerKnopf>}
      </div>
      {meldung && <div className="mt-3"><Hinweis art={meldung.art}>{meldung.text}</Hinweis></div>}
    </div>
  );
}
